import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import { WorkflowState } from '@compass/client/types/case_record';
import moment from 'moment';

export interface SLAResult {
  timeString: string;
  percentageElapsed: number;
  isBreached: boolean; // Backend flag or 100%+ elapsed
  remainingMinutes?: number; // Time remaining in minutes
}

/**
 * Format minutes as "HHh MMm" (e.g., 134 → "02h 14m", -82 → "-01h 22m").
 * Falls back to "Dd HHh MMm" for >= 24h and uses absolute value with leading sign for negatives.
 * The `h`/`m` suffixes (instead of `HH:MM`) avoid being mistaken for a time-of-day.
 */
export const formatMinutesAsHHMM = (minutes: number): string => {
  const isNegative = minutes < 0;
  const abs = Math.abs(Math.trunc(minutes));
  const days = Math.floor(abs / 1440);
  const hours = Math.floor((abs % 1440) / 60);
  const mins = abs % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(mins).padStart(2, '0');
  const core = days > 0 ? `${days}d ${hh}h ${mm}m` : `${hh}h ${mm}m`;
  return isNegative ? `-${core}` : core;
};

/**
 * Format elapsed time from minutes to human-readable string
 * @param elapsedMinutes - Time elapsed in minutes
 * @returns Formatted time string (e.g., "2h 30m" or "2d 3h 15m")
 */
export const formatElapsedTime = (elapsedMinutes: number): string => {
  const totalHours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;

  // If more than 24 hours (1440 minutes), show days format
  if (elapsedMinutes >= 1440) {
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    return `${days}d ${hours}h ${minutes}m`;
  }

  // For less than 24 hours, show standard hours and minutes
  return `${totalHours}h ${minutes}m`;
};

/**
 * Format remaining time from minutes to human-readable string
 * @param remainingMinutes - Time remaining in minutes (can be negative if overdue)
 * @returns Formatted remaining time string (e.g., "2h 30m" or "-1h 22m")
 */
export const formatRemainingTime = (remainingMinutes: number): string => {
  // Handle overdue (negative or zero remaining time)
  const isOverdue = remainingMinutes <= 0;
  const absoluteMinutes = Math.abs(remainingMinutes);

  const totalHours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;

  let timeString: string;

  // If more than 24 hours (1440 minutes), show days format
  if (absoluteMinutes >= 1440) {
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    timeString = `${days}d ${hours}h ${minutes}m`;
  } else {
    // For less than 24 hours, show standard hours and minutes
    timeString = `${totalHours}h ${minutes}m`;
  }

  // Add negative sign for overdue
  return isOverdue ? `-${timeString}` : timeString;
};

/**
 * Format SLA duration in compact form (e.g., 240 minutes → "4h")
 * @param slaMinutes - SLA duration in minutes
 * @returns Formatted SLA duration string
 */
export const formatSLADuration = (slaMinutes: number): string => {
  if (slaMinutes >= 1440) {
    const days = Math.floor(slaMinutes / 1440);
    const remainingMinutes = slaMinutes % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    if (hours > 0) {
      return `${days}d ${hours}h`;
    }
    return `${days}d`;
  }

  const hours = Math.floor(slaMinutes / 60);
  const minutes = slaMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
};

/**
 * Core SLA calculation logic
 * @param elapsedMinutes - Time elapsed in minutes
 * @param slaMinutes - SLA duration in minutes from workflow configuration
 * @param isBreached - Optional SLA breach flag from backend
 * @returns SLA calculation result
 */
const calculateSLAFromMinutes = (
  elapsedMinutes: number,
  slaMinutes: number | undefined,
  isBreached?: boolean,
): SLAResult => {
  // Calculate percentage if SLA is defined
  const percentageElapsed = slaMinutes ? (elapsedMinutes / slaMinutes) * 100 : 0;

  // Format elapsed time
  const timeString = formatElapsedTime(elapsedMinutes);

  // Check if SLA is breached
  const breached = slaMinutes ? elapsedMinutes >= slaMinutes : false;

  // Calculate remaining minutes
  const remainingMinutes = slaMinutes ? slaMinutes - elapsedMinutes : undefined;

  return {
    timeString,
    percentageElapsed,
    isBreached: breached || isBreached === true,
    remainingMinutes,
  };
};

/**
 * Calculate SLA elapsed time for a running case
 * @param workflowLastStartedAt - Workflow last started timestamp
 * @param workflowDurationSeconds - Accumulated workflow running time in seconds (excludes paused time)
 * @param slaMinutes - SLA duration in minutes from workflow configuration
 * @param isBreached - SLA breach flag from backend
 * @param workflowState - Current workflow state (if WAITING/paused, don't accumulate time)
 * @returns SLA calculation result with elapsed time string (wall-clock time minus paused time)
 */
export const calculateSLA = (
  workflowLastStartedAt: string | Date | undefined,
  workflowDurationSeconds: number | undefined,
  slaMinutes: number | undefined,
  isBreached?: boolean,
  workflowState?: WorkflowState,
): SLAResult => {
  // Return default if required data is missing
  if (!workflowLastStartedAt || !slaMinutes) {
    return {
      timeString: 'N/A',
      percentageElapsed: 0,
      isBreached: false,
    };
  }

  // Validate and parse the date
  const startTime = moment(workflowLastStartedAt);
  if (!startTime.isValid()) {
    // PHI-safe: do not log the timestamp value.
    console.error('Invalid workflow start date');
    return {
      timeString: 'Invalid date',
      percentageElapsed: 0,
      isBreached: false,
    };
  }

  // Calculate elapsed time
  // If workflow is paused (PAUSED state), don't use current time
  const isWorkflowPaused = workflowState === WorkflowState.WORKFLOW_STATE_PAUSED;
  const endTime = isWorkflowPaused ? startTime : moment();
  const currentWorkflowRuntime = endTime.diff(startTime, 'minutes');

  const accumulatedWorkflowRuntime =
    workflowDurationSeconds !== undefined ? Math.floor(workflowDurationSeconds / 60) : 0;

  const elapsedMinutes = currentWorkflowRuntime + accumulatedWorkflowRuntime;

  // Use shared calculation logic
  return calculateSLAFromMinutes(elapsedMinutes, slaMinutes, isBreached);
};

/**
 * Calculate SLA elapsed time for a completed case
 * @param workflowLastStartedAt - Workflow last started timestamp
 * @param completedAt - Workflow completion timestamp
 * @param workflowDurationSeconds - Accumulated workflow running time in seconds (excludes paused time)
 * @param slaMinutes - SLA duration in minutes from workflow configuration
 * @returns SLA calculation result for completed case (total time minus paused time)
 */
export const calculateCompletedSLA = (
  workflowLastStartedAt: string | Date | undefined,
  completedAt: string | Date | undefined,
  workflowDurationSeconds: number | undefined,
  slaMinutes: number | undefined,
): SLAResult => {
  // Return default if required data is missing
  if (!workflowLastStartedAt || !completedAt || !slaMinutes) {
    return {
      timeString: 'N/A',
      percentageElapsed: 0,
      isBreached: false,
    };
  }

  // Validate and parse dates
  const startTime = moment(workflowLastStartedAt);
  const endTime = moment(completedAt);

  if (!startTime.isValid() || !endTime.isValid()) {
    // PHI-safe: do not log the timestamp values.
    console.error('Invalid workflow date inputs');
    return {
      timeString: 'Invalid date',
      percentageElapsed: 0,
      isBreached: false,
    };
  }

  // Calculate total time from start to completion
  const currentWorkflowRuntime = endTime.diff(startTime, 'minutes');

  const accumulatedWorkflowRuntime =
    workflowDurationSeconds !== undefined ? Math.floor(workflowDurationSeconds / 60) : 0;

  const elapsedMinutes = accumulatedWorkflowRuntime || currentWorkflowRuntime;

  // Handle negative duration (shouldn't happen but defensive)
  if (elapsedMinutes < 0) {
    // PHI-safe: do not log durations or timestamp-derived values.
    console.warn('Negative elapsed time computed for completed case');
    return {
      timeString: '0h 0m',
      percentageElapsed: 0,
      isBreached: false,
    };
  }

  // Use shared calculation logic
  return calculateSLAFromMinutes(elapsedMinutes, slaMinutes);
};

/** Badge text shown when a case has breached its SLA. */
export const SLA_BREACHED_LABEL = 'SLA Breached';
/** Badge text shown when a case is close to breaching its SLA. */
export const SLA_BREACH_SOON_LABEL = 'SLA Breach Soon';

/** Non-PQC breach-soon threshold, as a percentage of the SLA budget elapsed. */
const NON_PQC_BREACH_WARNING_THRESHOLD = 87.5;

/**
 * Inputs the (non-PQC) SLA timer needs to decide what to display. Mirrors the
 * props LegacySLATimer receives; `slaMinutes` and `workflowVersion` come from the
 * case's *workflow*, everything else from the case record itself.
 */
export interface SlaParams {
  workflowLastStartedAt: string | undefined;
  workflowDurationSeconds: number | undefined;
  slaMinutes: number | undefined;
  /** Backend breach flag (status_flags.sla_breach). */
  isBreached: boolean | undefined;
  /** workflow_completed_at — when set, the case is treated as completed. */
  completedAt: string | undefined;
  workflowState: WorkflowState | undefined;
  /** Backend SLA-risk escalation flag (status_flags.sla_risk_escalated). */
  slaRiskEscalated: boolean | undefined;
  /** Workflow version (e.g. 'v3'), which changes how breach-soon is decided. */
  workflowVersion: string | undefined;
}

/** Workflow versions whose hermes workflow sets status_flags.sla_risk_escalated. */
const BACKEND_SLA_RISK_WORKFLOW_VERSIONS: readonly string[] = ['v3', 'v4'];

/**
 * True when the breach-soon signal for this workflow version is owned by the
 * backend (status_flags.sla_risk_escalated) rather than the elapsed-percentage
 * heuristic.
 */
export const isSlaRiskBackendOwned = (workflowVersion?: string): boolean =>
  BACKEND_SLA_RISK_WORKFLOW_VERSIONS.includes(workflowVersion ?? '');

export interface SlaValues {
  /** The timer text — remaining time while running, total turnaround once done. */
  slaTimer: string;
  /** The badge text: SLA_BREACHED_LABEL, SLA_BREACH_SOON_LABEL, or '' for none. */
  slaFlag: string;
}

/**
 * Resolves the SLA timer text and breach-flag text for a case, using the same
 * rules the non-PQC case-list timer renders (BV-1846).
 *
 * Extracted from LegacySLATimer so the CSV/Excel export and the table read from
 * one implementation and can never drift apart. Empty strings mean "show
 * nothing" — either the workflow never started, or there is no badge to show.
 * PHI-safe: performs no logging.
 */
export const computeSlaValues = (params: SlaParams): SlaValues => {
  // A workflow that never started has no elapsed time to report.
  if (!params.workflowLastStartedAt) {
    return { slaTimer: '', slaFlag: '' };
  }

  // Completed cases report total turnaround; only the backend breach flag applies.
  if (params.completedAt) {
    const completedInfo = calculateCompletedSLA(
      params.workflowLastStartedAt,
      params.completedAt,
      params.workflowDurationSeconds,
      params.slaMinutes,
    );
    return {
      slaTimer: completedInfo.timeString,
      slaFlag: params.isBreached ? SLA_BREACHED_LABEL : '',
    };
  }

  const info = calculateSLA(
    params.workflowLastStartedAt,
    params.workflowDurationSeconds,
    params.slaMinutes,
    params.isBreached,
    params.workflowState,
  );

  // Without an SLA budget there is no remaining time, so fall back to elapsed.
  const slaTimer =
    info.remainingMinutes !== undefined
      ? formatRemainingTime(info.remainingMinutes)
      : info.timeString;

  // For v3/v4 workflows the breach-soon signal is owned by the backend
  // (status_flags.sla_risk_escalated); the elapsed-percentage heuristic is
  // suppressed so the two signals never compete.
  const showBreachWarning = isSlaRiskBackendOwned(params.workflowVersion)
    ? params.slaRiskEscalated === true && !info.isBreached
    : info.percentageElapsed >= NON_PQC_BREACH_WARNING_THRESHOLD && !info.isBreached;

  let slaFlag = '';
  if (info.isBreached) {
    slaFlag = SLA_BREACHED_LABEL;
  } else if (showBreachWarning) {
    slaFlag = SLA_BREACH_SOON_LABEL;
  }

  return { slaTimer, slaFlag };
};

/**
 * Parameter bundle for time-remaining calculations. Keeps callers (e.g. the
 * Pending sort in My Cases) decoupled from the case-record shape.
 */
export interface CaseSlaParams {
  workflowLastStartedAt: string | undefined;
  workflowDurationSeconds: number | undefined;
  slaMinutes: number | undefined;
  workflowState: WorkflowState | undefined;
}

/**
 * Project a case record + workflow-SLA map onto the inputs the SLA timer needs.
 * Used by cross-program views (My Cases) where SLA minutes come from a per-row
 * workflow lookup rather than a single selectedWorkflow.
 */
export const buildCaseSlaParams = (
  item: GetCaseRecordResponse,
  slaByWorkflowId: Map<string, number>,
): CaseSlaParams => ({
  workflowLastStartedAt: item.workflow_last_started_at,
  workflowDurationSeconds: item.workflow_duration_seconds,
  slaMinutes: item.workflow_id ? slaByWorkflowId.get(item.workflow_id) : undefined,
  workflowState: item.workflow_state,
});

/**
 * Returns time remaining (in minutes) against the case's SLA, or `undefined`
 * when the case lacks the data needed to compute it. Suitable as a sort key —
 * negative values indicate breached cases (sort earlier than positive values).
 */
export const computeRemainingMinutes = (params: CaseSlaParams): number | undefined => {
  if (!params.workflowLastStartedAt || !params.slaMinutes) return undefined;
  const result = calculateSLA(
    params.workflowLastStartedAt,
    params.workflowDurationSeconds,
    params.slaMinutes,
    undefined,
    params.workflowState,
  );
  return result.remainingMinutes;
};
